import { useMemo, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Building2, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/layout/Header';
import { TownNavBar } from '@/components/layout/TownNavBar';
import { AreaNavBar } from '@/components/layout/AreaNavBar';
import { Footer } from '@/components/layout/Footer';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useTownsWithAreas, useRealtor, useBlogPosts, useTownGalleryImages } from '@/hooks/useHomePageData';
import { useLoanOfficerAd } from '@/hooks/useLoanOfficerAd';
import { useSiteAds, trackSiteAdClick } from '@/hooks/useSiteAds';
import { HomeImageCarousel } from '@/components/gallery/HomeImageCarousel';
import { PublicThemeProvider } from '@/components/theme/ThemeProvider';
import { sanitizeHtml } from '@/lib/sanitize';

// Lazy load below-fold components to reduce initial bundle size
const RealtorCard = lazy(() => import('@/components/realtor/RealtorCard').then(m => ({ default: m.RealtorCard })));
const LeadCaptureForm = lazy(() => import('@/components/forms/LeadCaptureForm').then(m => ({ default: m.LeadCaptureForm })));
const RecentBlogPostsCard = lazy(() => import('@/components/blog/RecentBlogPostsCard').then(m => ({ default: m.RecentBlogPostsCard })));

interface SiteData {
  id: string;
  name: string;
  location_served: string;
  tagline: string | null;
  description: string | null;
  theme: string;
  favicon_url: string | null;
  tenant_id: string | null;
  canonical_base_url: string | null;
  header_font: string | null;
  seller_ad_image_url: string | null;
  buyer_ad_image_url: string | null;
  mortgage_ad_image_url: string | null;
  enable_market_value_report?: boolean;
  enable_community_guide?: boolean;
  enable_mortgage_info?: boolean;
  bing_auth_code?: string | null;
  realtor_assigned?: boolean;
}

interface TownData {
  id: string;
  name: string;
  slug: string;
  state: string | null;
  intro_summary: string | null;
  hero_image_url: string | null;
  areas: AreaData[];
}

interface AreaData {
  id: string;
  name: string;
  slug: string;
  intro_summary: string | null;
}

interface RealtorData {
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  license_numbers: string | null;
  about_me: string | null;
  logo_url: string | null;
  title_preference?: 'agent' | 'realtor' | null;
}

interface BlogPostData {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  published_at: string;
  category: string;
  main_image_url: string | null;
}

interface SiteHomePageProps {
  site: SiteData;
}

export default function SiteHomePage({ site }: SiteHomePageProps) {
  // Initialize GA tracking with tenant_id
  useGoogleAnalytics(site.tenant_id);

  // React Query hooks with built-in caching (5 min stale, 30 min cache)
  const { towns, areas, isLoading: loading, areasLoading } = useTownsWithAreas(site.id);
  const { data: realtor } = useRealtor(site.id);
  const { data: blogPosts = [], isLoading: blogLoading } = useBlogPosts(site.id, 5);
  const { data: townGalleryImages = [] } = useTownGalleryImages(site.id);
  
  // Fetch active site ads from site_ads table (latest and greatest)
  const { data: siteAds } = useSiteAds(site.id);
  
  // Get town IDs for loan officer ad lookup
  const townIds = useMemo(() => towns.map(t => t.id), [towns]);
  const { data: loanOfficerAd } = useLoanOfficerAd(site.id, townIds);
  
  // Determine if we should show realtor content (only if realtor is assigned and exists)
  const showRealtorContent = site.realtor_assigned !== false && realtor;
  
  // Use active site ads from site_ads table, fall back to legacy fields on site object
  const sellerAd = siteAds?.seller;
  const buyerAd = siteAds?.buyer;
  const mortgageAdFromSiteAds = siteAds?.mortgage;
  
  const sellerAdImageUrl = sellerAd?.image_url || site.seller_ad_image_url;
  const buyerAdImageUrl = buyerAd?.image_url || site.buyer_ad_image_url;
  const mortgageAdImageUrlFromAds = mortgageAdFromSiteAds?.image_url || site.mortgage_ad_image_url;
  
  // Determine which mortgage ad to show (loan officer ad takes priority)
  const mortgageAdImageUrl = loanOfficerAd?.ad_image_url || mortgageAdImageUrlFromAds;
  const mortgageAdAlt = loanOfficerAd 
    ? `${loanOfficerAd.first_name} ${loanOfficerAd.last_name} - NMLS: ${loanOfficerAd.license_number || 'N/A'} - Mortgage Services`
    : 'Mortgage Information and Tips';
  
  // Click handlers for ads
  const handleSellerAdClick = () => {
    if (sellerAd?.id) trackSiteAdClick(sellerAd.id);
  };
  const handleBuyerAdClick = () => {
    if (buyerAd?.id) trackSiteAdClick(buyerAd.id);
  };
  const handleMortgageAdClick = () => {
    if (mortgageAdFromSiteAds?.id && !loanOfficerAd) {
      trackSiteAdClick(mortgageAdFromSiteAds.id);
    }
  };

  // Derived data
  const heroImageUrl = useMemo(() => {
    return towns.length > 0 ? towns[0].hero_image_url : null;
  }, [towns]);

  const totalAreas = useMemo(() => 
    towns.reduce((acc, town) => acc + town.areas.length, 0), 
    [towns]
  );

  const realtorName = showRealtorContent ? [realtor.first_name, realtor.last_name].filter(Boolean).join(' ') : '';

  // Prepare towns for navigation and footer
  const navTowns = towns.map(({ id, name, slug }) => ({ id, name, slug }));

  // Build canonical URL
  const canonicalUrl = site.canonical_base_url 
    ? `${site.canonical_base_url.replace(/\/$/, '')}/`
    : typeof window !== 'undefined' ? window.location.origin + '/' : '';

  // SEO title format: "Website Name | Location" (e.g., "Crippin Real Estate | South Coast, MA")
  const seoTitle = site.location_served 
    ? `${site.name} | ${site.location_served}`
    : site.name;

  // SEO description priority: description field > tagline > auto-generated
  const seoDescription = site.description 
    || site.tagline 
    || `${realtorName ? `${realtorName} is your trusted` : 'Your trusted'} real estate partner serving ${site.location_served}. Explore homes and properties across ${towns.length} communities.`;

  // Build list of town names for text summary
  const townNames = towns.map(t => `${t.name}${t.state ? `, ${t.state}` : ''}`);
  const locationSummary = townNames.length > 0
    ? townNames.length <= 3
      ? townNames.join(', ')
      : `${townNames.slice(0, 3).join(', ')}, and ${townNames.length - 3} more`
    : site.location_served;

  // Extract domain for alternateName (Google site name recognition)
  const domainName = canonicalUrl ? canonicalUrl.replace(/^https?:\/\//, '').replace(/\/$/, '') : '';

  // Structured data for home page
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${canonicalUrl}/#website`,
    "name": site.name,
    "alternateName": domainName || undefined,
    "url": canonicalUrl,
    "description": seoDescription,
    "publisher": {
      "@type": "RealEstateAgent",
      "@id": `${canonicalUrl}/#agency`
    }
  };

  const realEstateAgentSchema = showRealtorContent && realtorName ? {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": realtorName,
    "description": realtor.about_me || `Real estate agent serving ${site.location_served}`,
    "url": canonicalUrl,
    "telephone": realtor.phone || undefined,
    "image": realtor.photo_url || undefined,
    "address": realtor.city ? {
      "@type": "PostalAddress",
      "addressLocality": realtor.city,
      "addressRegion": realtor.state || undefined,
      "addressCountry": "US"
    } : undefined,
    "areaServed": towns.map(t => ({
      "@type": "City",
      "name": t.name,
      "containedInPlace": t.state ? {
        "@type": "State",
        "name": t.state
      } : undefined
    }))
  } : null;

  return (
    <PublicThemeProvider themeId={site.theme || 'classic'} headerFontId={site.header_font || 'playfair-display'}>
      <div className="min-h-screen flex flex-col">
        <Helmet>
          <title>{seoTitle}</title>
          <meta name="description" content={seoDescription} />
          <link rel="canonical" href={canonicalUrl} />
          <meta name="robots" content="index, follow" />
          
          {/* Bing Verification */}
          {site.bing_auth_code && <meta name="msvalidate.01" content={site.bing_auth_code} />}
          
          {/* Preload LCP hero image with high priority to reduce resource load delay */}
          {heroImageUrl && <link rel="preload" as="image" href={heroImageUrl} fetchPriority="high" />}
          
          {/* Open Graph */}
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content={site.name} />
          <meta property="og:title" content={seoTitle} />
          <meta property="og:description" content={seoDescription} />
          {/* og:url and og:image are injected by Cloudflare worker */}
          
          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={seoTitle} />
          <meta name="twitter:description" content={seoDescription} />

          {/* Structured Data */}
          <script type="application/ld+json">
            {JSON.stringify(websiteSchema)}
          </script>
          {realEstateAgentSchema && (
            <script type="application/ld+json">
              {JSON.stringify(realEstateAgentSchema)}
            </script>
          )}
        </Helmet>

        <Header
          siteName={site.name} 
          logoUrl={showRealtorContent ? realtor.logo_url : undefined} 
          faviconUrl={site.favicon_url}
          enableMarketValueReport={site.enable_market_value_report !== false}
          enableCommunityGuide={site.enable_community_guide !== false}
          enableMortgageInfo={site.enable_mortgage_info !== false}
        />
        
        {/* Town Navigation Bar */}
        <TownNavBar 
          towns={navTowns}
          enableMarketValueReport={site.enable_market_value_report !== false}
          enableMortgageInfo={site.enable_mortgage_info !== false}
          realtorFirstName={realtor?.first_name}
          themeId={site.theme}
        />
        
        {/* Area Navigation Bar - Show when only one town exists */}
        {towns.length === 1 && towns[0].areas.length > 0 && (
          <AreaNavBar 
            areas={towns[0].areas.map(a => ({ id: a.id, name: a.name, slug: a.slug }))} 
            townSlug={towns[0].slug} 
          />
        )}
        
        <main className="flex-1 main-content-critical">
          {/* Hero Section - Rendered immediately with site data (no API wait) */}
          <section 
            className="relative bg-gradient-hero text-white py-6 md:py-10 hero-section-critical"
            style={heroImageUrl ? {
              backgroundImage: `linear-gradient(to bottom, rgba(30, 41, 59, 0.85), rgba(30, 41, 59, 0.95)), url(${heroImageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            } : undefined}
          >
            <div className="container px-4 sm:px-6 lg:px-8 relative z-10">
              <nav className="flex items-center gap-2 text-sm mb-6 opacity-80">
                <span>Home</span>
              </nav>
              <h1 className="text-2xl md:text-3xl font-display font-bold mb-2">
                {/* Use site data directly - available immediately, no API wait */}
                {`Real Estate Services in ${site.location_served}`}
              </h1>
              <h2 className="text-2xl md:text-3xl font-display font-medium mb-4 opacity-90">
                {site.tagline || `Your Trusted Real Estate Partner`}
              </h2>
              {site.description && (
                <p className="text-xl opacity-80">
                  {site.description}
                </p>
              )}
            </div>
          </section>

          {/* About Section */}
          <section className="py-6 md:py-8">
            <div className="container px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-10 gap-8">
                {/* Left: About content - 70% */}
                <div className="lg:col-span-7">
                  {/* Town Images Carousel - Reserve space with min-height to prevent CLS */}
                  <div className="mb-8 min-h-[225px] sm:min-h-[300px] md:min-h-[350px]">
                    {townGalleryImages.length > 0 ? (
                      <HomeImageCarousel images={townGalleryImages} autoPlayInterval={3000} />
                    ) : (
                      /* Skeleton placeholder while loading */
                      <div className="w-full aspect-video rounded-lg bg-muted animate-pulse" />
                    )}
                  </div>

                  <h2 className="text-3xl font-display font-bold mb-6">
                    {realtorName ? `About ${realtorName}` : 'About Us'}
                  </h2>
                  
                  {showRealtorContent && realtor.about_me ? (
                    <div 
                      className="prose prose-lg max-w-none text-muted-foreground [&>p]:mb-4 [&>p:last-child]:mb-0"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(realtor.about_me) }}
                    />
                  ) : (
                    <p className="text-muted-foreground">
                      {realtorName || 'Your dedicated real estate team'} is committed to helping you find the perfect home in {site.location_served}. 
                      With deep knowledge of the local market and a passion for real estate, 
                      {realtorName ? ` ${realtorName.split(' ')[0]}` : ' we'} provide personalized service to buyers and sellers alike.
                    </p>
                  )}

                  {/* Service Areas Summary - Reserve min-height for loading state */}
                  <div className="mt-8 bg-secondary/30 rounded-lg p-6 min-h-[150px]">
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-primary" />
                      Areas Served
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {loading ? (
                        <span className="inline-block w-full h-4 bg-muted rounded animate-pulse" />
                      ) : (
                        <>
                          {realtorName || 'We'} proudly serve{realtorName ? 's' : ''} clients across {towns.length > 1 ? `${towns.length} communities` : 'communities'} in {site.location_served},
                          including {locationSummary}.
                          {totalAreas > 0 && ` With expertise in ${totalAreas > 1 ? `${totalAreas} distinct neighborhoods` : 'distinct neighborhoods'}, you'll get hyper-local knowledge that makes all the difference.`}
                        </>
                      )}
                    </p>
                    
                    {/* Town List */}
                    {loading ? (
                      <div className="flex flex-wrap gap-2">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="h-8 w-24 bg-muted rounded-full animate-pulse" />
                        ))}
                      </div>
                    ) : towns.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {towns.map((town) => (
                          <Link
                            key={town.id}
                            to={`/${town.slug}`}
                            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 bg-background rounded-full hover:bg-primary/10 transition-colors border"
                          >
                            <MapPin className="h-3 w-3" />
                            {town.name}
                            {town.areas.length > 0 && (
                              <span className="text-xs text-muted-foreground ml-1">
                                ({town.areas.length})
                              </span>
                            )}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Market Value & Mortgage Ads - Side by side below Areas Served */}
                  {((site.enable_market_value_report !== false && sellerAdImageUrl) ||
                    (site.enable_mortgage_info !== false && mortgageAdImageUrl)) && (
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {site.enable_market_value_report !== false && sellerAdImageUrl && (
                        <Link to="/market-value-report" className="rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow block" onClick={handleSellerAdClick}>
                          <img 
                            src={sellerAdImageUrl} 
                            alt="Free Local Market Value Report for Home Sellers" 
                            className="w-full h-auto aspect-square object-cover"
                            loading="lazy"
                            decoding="async"
                            width={400}
                            height={400}
                          />
                        </Link>
                      )}
                      {site.enable_mortgage_info !== false && mortgageAdImageUrl && (
                        <Link to="/mortgages" className="rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow block" onClick={handleMortgageAdClick}>
                          <img 
                            src={mortgageAdImageUrl} 
                            alt={mortgageAdAlt} 
                            className="w-full h-auto aspect-square object-cover"
                            loading="lazy"
                            decoding="async"
                            width={400}
                            height={400}
                          />
                        </Link>
                      )}
                    </div>
                  )}

                </div>

                {/* Right: Sidebar - 30% */}
                <aside className="lg:col-span-3 space-y-6">
                  {/* Recent Blog Posts - Lazy loaded */}
                  <Suspense fallback={<Card className="p-6 lg:p-8"><div className="h-40 animate-pulse bg-muted rounded" /></Card>}>
                    <RecentBlogPostsCard posts={blogPosts} title="Latest Articles" loading={blogLoading} />
                  </Suspense>

                  {/* Realtor Card - Lazy loaded */}
                  {showRealtorContent && (
                    <Suspense fallback={<Card className="p-6"><div className="h-32 animate-pulse bg-muted rounded" /></Card>}>
                      <RealtorCard realtor={realtor} />
                    </Suspense>
                  )}

                  <Card className="p-6 lg:p-8">
                    <h3 className="text-2xl font-display font-bold mb-2 text-center">
                      Get in Touch
                    </h3>
                    <p className="text-muted-foreground text-center mb-6">
                      {realtorName 
                        ? `Contact ${realtorName} to learn more about real estate in ${site.location_served}`
                        : `Contact us to learn more about real estate in ${site.location_served}`
                      }
                    </p>
                    <Suspense fallback={<div className="h-48 animate-pulse bg-muted rounded" />}>
                      <LeadCaptureForm 
                        siteId={site.id} 
                        siteDomain={window.location.hostname}
                        pagePath="/"
                      />
                    </Suspense>
                  </Card>
                </aside>
              </div>
            </div>
          </section>

          {/* Towns & Areas Section */}
          <section className="py-12 md:py-16 bg-muted">
            <div className="container px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-display font-bold mb-8">
                {towns.length === 1 ? `Explore Neighborhoods in ${towns[0].name}` : 'Explore Our Communities'}
              </h2>

              {towns.length === 0 && !loading ? (
                <p className="text-center text-muted-foreground py-8">
                  No communities are currently available. Check back soon!
                </p>
              ) : towns.length === 1 && towns[0].areas.length > 0 ? (
                /* Single town: Show areas/neighborhoods directly */
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {towns[0].areas.map((area) => (
                    <Card key={area.id} className="hover:shadow-lg transition-shadow bg-card border-border">
                      <CardContent className="p-6">
                        <Link to={`/${towns[0].slug}/${area.slug}`} className="block group">
                          <div className="flex items-start gap-3 mb-3">
                            <div className="p-2 rounded-full bg-primary/10 shrink-0">
                              <Building2 className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <h3 className="text-xl font-semibold text-card-foreground group-hover:text-primary transition-colors">
                                {area.name}
                              </h3>
                            </div>
                          </div>
                          {area.intro_summary && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                              {area.intro_summary}
                            </p>
                          )}
                        </Link>
                        <Link to={`/${towns[0].slug}/${area.slug}`}>
                          <Button variant="ghost" size="sm" className="mt-4 w-full group">
                            View {area.name}
                            <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                /* Multiple towns: Show town cards with nested areas */
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {towns.map((town) => (
                    <Card key={town.id} className="hover:shadow-lg transition-shadow bg-background">
                      <CardContent className="p-6">
                        <Link to={`/${town.slug}`} className="block group">
                          <div className="flex items-start gap-3 mb-3">
                            <div className="p-2 rounded-full bg-primary/10 shrink-0">
                              <MapPin className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <h3 className="text-xl font-semibold group-hover:text-primary transition-colors">
                                {town.name}
                                {town.state && (
                                  <span className="text-muted-foreground font-normal">, {town.state}</span>
                                )}
                              </h3>
                            </div>
                          </div>
                          {town.intro_summary && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                              {town.intro_summary}
                            </p>
                          )}
                        </Link>

                        {/* Areas List */}
                        {town.areas.length > 0 && (
                          <div className="mt-4 pt-4 border-t">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                              <Building2 className="h-4 w-4" />
                              <span>{town.areas.length} Neighborhood{town.areas.length !== 1 ? 's' : ''}</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {town.areas.slice(0, 4).map((area) => (
                                <Link
                                  key={area.id}
                                  to={`/${town.slug}/${area.slug}`}
                                  className="text-xs px-2 py-1 bg-muted border border-border rounded-full hover:bg-accent transition-colors"
                                >
                                  {area.name}
                                </Link>
                              ))}
                              {town.areas.length > 4 && (
                                <span className="text-xs px-2 py-1 text-muted-foreground">
                                  +{town.areas.length - 4} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        <Link to={`/${town.slug}`}>
                          <Button variant="ghost" size="sm" className="mt-4 w-full group">
                            View {town.name}
                            <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Ad Images Section - Above Footer (Community Guide only) */}
          {site.enable_community_guide !== false && buyerAdImageUrl && (
            <section className="py-8 md:py-12 bg-background">
              <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-md mx-auto">
                  <Link to="/community-guide" className="rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow block" onClick={handleBuyerAdClick}>
                    <img 
                      src={buyerAdImageUrl} 
                      alt="Free Local Buyer Community Guide" 
                      className="w-full h-auto aspect-square object-cover"
                    />
                  </Link>
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer
          siteName={site.name} 
          locationServed={site.location_served}
          towns={navTowns}
          areas={areas}
          realtor={showRealtorContent ? realtor : null}
          enableBuyers={true}
          enableSellers={true}
          enableMortgageInfo={site.enable_mortgage_info !== false}
        />
      </div>
    </PublicThemeProvider>
  );
}
